// src/du-runtime/du-agent.ts

export type DuRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type DuElementInfo = {
  id: string;
  tag: string;
  text?: string;
  role?: string;
  type?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  ariaLabel?: string;
  selector: string;
  visible: boolean;
  rect: DuRect;
};

export type DuNetworkEvent = {
  id: string;
  timestamp: number;
  method: string;
  url: string;
  status?: number;
  ok?: boolean;
  durationMs?: number;
  requestBody?: unknown;
  responseBody?: unknown;
  error?: string;
};

export type DuUiEvent = {
  id: string;
  timestamp: number;
  type: string;
  url: string;
  target?: DuElementInfo;
  value?: unknown;
  key?: string;
  data?: unknown;
};

export type DuAgentExport = {
  generatedAt: string;
  url: string;
  elements: DuElementInfo[];
  events: DuUiEvent[];
  network: DuNetworkEvent[];
};

export type DuAgentOptions = {
  securitySafe?: boolean;
};

export type DuAgentApi = {
  start: () => void;
  stop: () => void;

  scan: () => DuElementInfo[];
  events: () => DuUiEvent[];
  network: () => DuNetworkEvent[];
  export: () => DuAgentExport;

  wait: (ms: number) => Promise<void>;
  waitForText: (text: string, timeoutMs?: number) => Promise<DuElementInfo>;
  waitForSelector: (selector: string, timeoutMs?: number) => Promise<HTMLElement>;

  click: (target: string | HTMLElement) => Promise<void>;
  clickByText: (text: string) => Promise<void>;
  fill: (target: string | HTMLElement, value: string) => Promise<void>;
  fillByLabel: (label: string, value: string) => Promise<void>;
  select: (target: string | HTMLElement, value: string) => Promise<void>;
  hover: (target: string | HTMLElement) => Promise<void>;
  scrollTo: (target: string | HTMLElement) => Promise<void>;

  moveCursorTo: (target: string | HTMLElement) => Promise<void>;

  clear: () => void;

  generateCpf: (formatted?: boolean) => string;
  generateCnpj: (formatted?: boolean) => string;
  generateEmail: (profile?: string) => string;
  generateName: () => string;
  getPassword: () => string;
  getPhone: () => string;
};

declare global {
  interface Window {
    du?: DuAgentApi;
  }
}

const DU_ID_ATTR = "data-du-id";
const DU_MARK_ATTR = "data-du-marked";

let originalFetch: typeof window.fetch | undefined;
let originalPushState: History["pushState"] | undefined;
let originalReplaceState: History["replaceState"] | undefined;

function createId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function isHTMLElement(value: unknown): value is HTMLElement {
  return value instanceof HTMLElement;
}

function isInputLike(
  element: HTMLElement
): element is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement {
  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement
  );
}

function isVisible(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);

  return (
    rect.width > 0 &&
    rect.height > 0 &&
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    Number(style.opacity) !== 0
  );
}

function getText(element: HTMLElement): string | undefined {
  const text = element.innerText?.replace(/\s+/g, " ").trim();
  return text ? text.slice(0, 300) : undefined;
}

function getSelector(element: HTMLElement): string {
  const duId = element.getAttribute(DU_ID_ATTR);

  if (duId) {
    return `[${DU_ID_ATTR}="${duId}"]`;
  }

  if (element.id) {
    return `#${CSS.escape(element.id)}`;
  }

  const parts: string[] = [];
  let current: HTMLElement | null = element;

  while (current && current !== document.body && parts.length < 5) {
    const parent = current.parentElement || (current.getRootNode() as ShadowRoot).host;
    let part = current.tagName.toLowerCase();

    if (current.id) {
      part += `#${CSS.escape(current.id)}`;
      parts.unshift(part);
      break;
    }

    const classes = Array.from(current.classList)
      .slice(0, 2)
      .map((className) => `.${CSS.escape(className)}`)
      .join("");

    part += classes;

    if (parent) {
      const sameTagSiblings = Array.from(parent.children || []).filter(
        (child) => child.tagName === current?.tagName
      );

      if (sameTagSiblings.length > 1) {
        part += `:nth-of-type(${sameTagSiblings.indexOf(current) + 1})`;
      }
    }

    parts.unshift(part);

    if (current.parentElement) {
      current = current.parentElement;
    } else {
      const root = current.getRootNode();
      if (root instanceof ShadowRoot) {
        current = root.host as HTMLElement;
      } else {
        current = null;
      }
    }
  }

  return parts.join(" > ");
}

function getAssociatedLabel(element: HTMLElement): string | undefined {
  if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement)) {
    return undefined;
  }

  if (element.id) {
    const label = document.querySelector<HTMLLabelElement>(
      `label[for="${CSS.escape(element.id)}"]`
    );

    if (label?.innerText) {
      return label.innerText.replace(/\s+/g, " ").trim();
    }
  }

  const parentLabel = element.closest("label");

  if (parentLabel?.innerText) {
    return parentLabel.innerText.replace(/\s+/g, " ").trim();
  }

  return undefined;
}

function ensureElementId(element: HTMLElement): string {
  const existing = element.getAttribute(DU_ID_ATTR);

  if (existing) return existing;

  const id = createId("du_el");
  element.setAttribute(DU_ID_ATTR, id);

  return id;
}

function getElementInfo(element: HTMLElement): DuElementInfo {
  const id = ensureElementId(element);
  const rect = element.getBoundingClientRect();

  return {
    id,
    tag: element.tagName.toLowerCase(),
    text: getText(element),
    role: element.getAttribute("role") || undefined,
    type:
      element instanceof HTMLInputElement
        ? element.type
        : element.getAttribute("type") || undefined,
    name:
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement ||
      element instanceof HTMLSelectElement ||
      element instanceof HTMLButtonElement
        ? element.name || undefined
        : element.getAttribute("name") || undefined,
    label: getAssociatedLabel(element),
    placeholder:
      element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement
        ? element.placeholder || undefined
        : undefined,
    ariaLabel: element.getAttribute("aria-label") || undefined,
    selector: getSelector(element),
    visible: isVisible(element),
    rect: {
      x: Math.round(rect.x),
      y: Math.round(rect.y),
      width: Math.round(rect.width),
      height: Math.round(rect.height)
    }
  };
}

function shouldMark(element: HTMLElement): boolean {
  const tag = element.tagName.toLowerCase();

  if (
    [
      "button",
      "input",
      "textarea",
      "select",
      "a",
      "form",
      "dialog",
      "summary",
      "label"
    ].includes(tag)
  ) {
    return true;
  }

  if (element.hasAttribute("role")) return true;
  if (element.hasAttribute("aria-label")) return true;
  if (element.hasAttribute("data-testid")) return true;
  if (element.hasAttribute("data-cy")) return true;
  if (element.hasAttribute("data-test")) return true;
  if (element.onclick) return true;

  return false;
}

function markDom(root: ParentNode = document): void {
  const elements = Array.from(root.querySelectorAll<HTMLElement>("*"));

  for (const element of elements) {
    if (element.hasAttribute(DU_MARK_ATTR)) continue;

    element.setAttribute(DU_MARK_ATTR, "1");

    if (shouldMark(element)) {
      ensureElementId(element);
    }

    if (element.shadowRoot) {
      markDom(element.shadowRoot);
    }
  }
}

function findElementByDuId(id: string, root: ParentNode = document): HTMLElement | null {
  const el = root.querySelector<HTMLElement>(`[${DU_ID_ATTR}="${id}"]`);
  if (el) return el;

  const all = Array.from(root.querySelectorAll<HTMLElement>("*"));
  for (const item of all) {
    if (item.shadowRoot) {
      const found = findElementByDuId(id, item.shadowRoot);
      if (found) return found;
    }
  }
  return null;
}

function findElementDeep(selector: string, root: ParentNode = document): HTMLElement | null {
  const el = root.querySelector<HTMLElement>(selector);
  if (el) return el;

  const all = Array.from(root.querySelectorAll<HTMLElement>("*"));
  for (const item of all) {
    if (item.shadowRoot) {
      const found = findElementDeep(selector, item.shadowRoot);
      if (found) return found;
    }
  }
  return null;
}

function findTarget(target: string | HTMLElement): HTMLElement {
  if (isHTMLElement(target)) return target;

  const duIdMatch = target.match(/^\[data-du-id="([^"]+)"\]$/);
  if (duIdMatch) {
    const found = findElementByDuId(duIdMatch[1]);
    if (found) return found;
  }

  const element = document.querySelector<HTMLElement>(target);
  if (element) return element;

  const deepFound = findElementDeep(target);
  if (deepFound) return deepFound;

  throw new Error(`Elemento não encontrado: ${target}`);
}

function findByText(text: string, root: ParentNode = document): HTMLElement {
  const normalized = text.toLowerCase().trim();
  const candidates: HTMLElement[] = [];

  function collectCandidates(node: ParentNode) {
    const items = Array.from(
      node.querySelectorAll<HTMLElement>(
        "button,a,[role='button'],[role='menuitem'],label,summary"
      )
    ).filter(isVisible);

    candidates.push(...items);

    const all = Array.from(node.querySelectorAll<HTMLElement>("*"));
    for (const el of all) {
      if (el.shadowRoot) {
        collectCandidates(el.shadowRoot);
      }
    }
  }

  collectCandidates(root);

  const found = candidates.find((element) =>
    (element.innerText || "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()
      .includes(normalized)
  );

  if (!found) {
    throw new Error(`Texto não encontrado: ${text}`);
  }

  return found;
}

function findByLabel(labelText: string, root: ParentNode = document): HTMLElement {
  const normalized = labelText.toLowerCase().trim();
  const fields: HTMLElement[] = [];

  function collectFields(node: ParentNode) {
    const items = Array.from(
      node.querySelectorAll<HTMLElement>("input,textarea,select")
    ).filter(isVisible);

    fields.push(...items);

    const all = Array.from(node.querySelectorAll<HTMLElement>("*"));
    for (const el of all) {
      if (el.shadowRoot) {
        collectFields(el.shadowRoot);
      }
    }
  }

  collectFields(root);

  const found = fields.find((field) => {
    const info = getElementInfo(field);

    return [
      info.label,
      info.placeholder,
      info.ariaLabel,
      info.name
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalized));
  });

  if (!found) {
    throw new Error(`Campo não encontrado pelo label: ${labelText}`);
  }

  return found;
}

function dispatchMouseEvent(
  element: HTMLElement,
  type: "mouseover" | "mousemove" | "mousedown" | "mouseup" | "click"
): void {
  const rect = element.getBoundingClientRect();
  const x = rect.x + rect.width / 2;
  const y = rect.y + rect.height / 2;

  if (window.PointerEvent) {
    let pointerType = "";
    if (type === "mousedown") pointerType = "pointerdown";
    else if (type === "mouseup") pointerType = "pointerup";
    else if (type === "mouseover") pointerType = "pointerover";
    else if (type === "mousemove") pointerType = "pointermove";

    if (pointerType) {
      const pointerEvent = new PointerEvent(pointerType, {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: x,
        clientY: y,
        isPrimary: true
      });
      element.dispatchEvent(pointerEvent);
    }
  }

  const mouseEvent = new MouseEvent(type, {
    bubbles: true,
    cancelable: true,
    view: window,
    clientX: x,
    clientY: y
  });

  element.dispatchEvent(mouseEvent);
}

function setNativeValue(element: HTMLElement, value: string): void {
  if (element instanceof HTMLInputElement) {
    const prototype = Object.getPrototypeOf(element);
    const descriptor = Object.getOwnPropertyDescriptor(prototype, "value");

    descriptor?.set?.call(element, value);

    element.dispatchEvent(new Event("input", { bubbles: true }));
    element.dispatchEvent(new Event("change", { bubbles: true }));

    return;
  }

  if (element instanceof HTMLTextAreaElement) {
    const prototype = Object.getPrototypeOf(element);
    const descriptor = Object.getOwnPropertyDescriptor(prototype, "value");

    descriptor?.set?.call(element, value);

    element.dispatchEvent(new Event("input", { bubbles: true }));
    element.dispatchEvent(new Event("change", { bubbles: true }));

    return;
  }

  if (element instanceof HTMLSelectElement) {
    element.value = value;

    element.dispatchEvent(new Event("input", { bubbles: true }));
    element.dispatchEvent(new Event("change", { bubbles: true }));

    return;
  }

  throw new Error(`Elemento não aceita valor: ${element.tagName}`);
}

function createCursor(): HTMLElement {
  let cursor = document.getElementById("du-runtime-cursor") as HTMLElement | null;

  if (cursor) return cursor;

  cursor = document.createElement("div");
  cursor.id = "du-runtime-cursor";

  Object.assign(cursor.style, {
    position: "fixed",
    zIndex: "2147483647",
    left: "0px",
    top: "0px",
    width: "18px",
    height: "18px",
    borderRadius: "999px",
    background: "rgba(0, 0, 0, 0.75)",
    border: "2px solid white",
    boxShadow: "0 0 12px rgba(0,0,0,.35)",
    pointerEvents: "none",
    transform: "translate(-50%, -50%)",
    transition: "left 220ms ease, top 220ms ease"
  });

  document.body.appendChild(cursor);

  return cursor;
}

function getEventTargetInfo(event: Event): DuElementInfo | undefined {
  const target = event.target;

  if (!isHTMLElement(target)) return undefined;

  const closest = target.closest<HTMLElement>(
    `[${DU_ID_ATTR}],button,input,textarea,select,a,[role],[aria-label],[data-testid],[data-cy],[data-test]`
  );

  if (!closest) return getElementInfo(target);

  return getElementInfo(closest);
}

function sanitizeValue(element: HTMLElement, value: string): string {
  if (element instanceof HTMLInputElement && element.type === "password") {
    return "***";
  }

  const name = (element.getAttribute("name") || "").toLowerCase();
  const placeholder = (element.getAttribute("placeholder") || "").toLowerCase();
  const id = (element.id || "").toLowerCase();

  if (
    name.includes("senha") || name.includes("password") ||
    name.includes("token") || name.includes("cpf") ||
    name.includes("documento") || name.includes("telefone") ||
    placeholder.includes("senha") || placeholder.includes("cpf") ||
    id.includes("password") || id.includes("senha")
  ) {
    return "***";
  }

  return value;
}

function safeJson(value: unknown): unknown {
  try {
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return value.slice(0, 1000);
      }
    }

    if (value instanceof FormData) {
      return "[FormData]";
    }

    if (value instanceof Blob) {
      return `[Blob ${value.type}]`;
    }

    return value;
  } catch {
    return undefined;
  }
}

export function installDuRuntimeAgent(options: DuAgentOptions = {}): DuAgentApi {
  let started = false;
  let observer: MutationObserver | undefined;
  const securitySafe = options.securitySafe === true;

  const uiEvents: DuUiEvent[] = [];
  const networkEvents: DuNetworkEvent[] = [];

  function recordUi(event: Omit<DuUiEvent, "id" | "timestamp" | "url">): void {
    uiEvents.push({
      id: createId("du_evt"),
      timestamp: Date.now(),
      url: window.location.href,
      ...event
    });
  }

  function recordNetwork(event: Omit<DuNetworkEvent, "id" | "timestamp">): void {
    networkEvents.push({
      id: createId("du_net"),
      timestamp: Date.now(),
      ...event
    });
  }

  function scan(): DuElementInfo[] {
    return scanDom(document);
  }

  function scanDom(root: ParentNode): DuElementInfo[] {
    markDom(root);

    const found: DuElementInfo[] = [];

    function collect(node: ParentNode) {
      const elements = Array.from(node.querySelectorAll<HTMLElement>(`[${DU_ID_ATTR}]`));
      for (const el of elements) {
        const info = getElementInfo(el);
        if (info.visible) {
          found.push(info);
        }
      }

      const all = Array.from(node.querySelectorAll<HTMLElement>("*"));
      for (const el of all) {
        if (el.shadowRoot) {
          collect(el.shadowRoot);
        }
      }
    }

    collect(root);
    return found;
  }

  function patchFetch(): void {
    if (originalFetch) return;

    originalFetch = window.fetch.bind(window);

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const startedAt = performance.now();

      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.toString()
            : input.url;

      const method =
        init?.method ??
        (input instanceof Request ? input.method : "GET");

      try {
        const response = await originalFetch!(input, init);
        let responseBody: unknown = undefined;
        if (!securitySafe) {
          const cloned = response.clone();
          const contentType = cloned.headers.get("content-type") ?? "";

          try {
            if (contentType.includes("application/json")) {
              responseBody = await cloned.json();
            } else if (contentType.includes("text/")) {
              responseBody = (await cloned.text()).slice(0, 2000);
            }
          } catch {
            responseBody = undefined;
          }
        }

        recordNetwork({
          method,
          url,
          status: response.status,
          ok: response.ok,
          durationMs: Math.round(performance.now() - startedAt),
          ...(securitySafe ? {} : {
            requestBody: safeJson(init?.body),
            responseBody
          })
        });

        return response;
      } catch (error) {
        recordNetwork({
          method,
          url,
          durationMs: Math.round(performance.now() - startedAt),
          ...(securitySafe ? {} : {
            requestBody: safeJson(init?.body)
          }),
          error: error instanceof Error ? error.message : String(error)
        });

        throw error;
      }
    };
  }

  function unpatchFetch(): void {
    if (!originalFetch) return;

    window.fetch = originalFetch;
    originalFetch = undefined;
  }

  function patchHistory(): void {
    if (originalPushState || originalReplaceState) return;

    originalPushState = history.pushState;
    originalReplaceState = history.replaceState;

    history.pushState = function patchedPushState(...args) {
      const result = originalPushState!.apply(this, args);

      queueMicrotask(() => {
        recordUi({
          type: "route",
          data: {
            href: window.location.href
          }
        });
      });

      return result;
    };

    history.replaceState = function patchedReplaceState(...args) {
      const result = originalReplaceState!.apply(this, args);

      queueMicrotask(() => {
        recordUi({
          type: "route",
          data: {
            href: window.location.href
          }
        });
      });

      return result;
    };

    window.addEventListener(
      "popstate",
      () => {
        recordUi({
          type: "route",
          data: {
            href: window.location.href
          }
        });
      },
      true
    );
  }

  function unpatchHistory(): void {
    if (originalPushState) {
      history.pushState = originalPushState;
      originalPushState = undefined;
    }

    if (originalReplaceState) {
      history.replaceState = originalReplaceState;
      originalReplaceState = undefined;
    }
  }

  function handleClick(event: MouseEvent): void {
    recordUi({
      type: "click",
      target: getEventTargetInfo(event)
    });
  }

  function handleInput(event: Event): void {
    const target = event.target;

    if (isHTMLElement(target) && isInputLike(target)) {
      let value: unknown;
      if (target instanceof HTMLInputElement && (target.type === "checkbox" || target.type === "radio")) {
        value = target.checked ? "checked" : "unchecked";
      } else {
        value = sanitizeValue(target, target.value);
      }
      recordUi({
        type: "input",
        target: getEventTargetInfo(event),
        value
      });
    }
  }

  function handleChange(event: Event): void {
    const target = event.target;

    if (isHTMLElement(target) && isInputLike(target)) {
      let value: unknown;
      if (target instanceof HTMLInputElement && (target.type === "checkbox" || target.type === "radio")) {
        value = target.checked ? "checked" : "unchecked";
      } else {
        value = sanitizeValue(target, target.value);
      }
      recordUi({
        type: "change",
        target: getEventTargetInfo(event),
        value
      });
    }
  }

  function handleSubmit(event: Event): void {
    recordUi({
      type: "submit",
      target: getEventTargetInfo(event)
    });
  }

  function handleKeydown(event: KeyboardEvent): void {
    const target = event.target;
    // Filtra para capturar apenas teclas de navegação ou submit para não sobrecarregar
    if (["Enter", "Tab", "Escape", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
      let keyVal = event.key;
      if (isHTMLElement(target) && target instanceof HTMLInputElement && target.type === "password") {
        keyVal = "*";
      }
      recordUi({
        type: "keydown",
        target: getEventTargetInfo(event),
        key: keyVal
      });
    }
  }

  function start(): void {
    if (started) return;

    started = true;

    markDom();

    document.addEventListener("click", handleClick, true);
    document.addEventListener("input", handleInput, true);
    document.addEventListener("change", handleChange, true);
    document.addEventListener("submit", handleSubmit, true);
    document.addEventListener("keydown", handleKeydown, true);

    let mutationTimeout: number | undefined;
    observer = new MutationObserver((mutations) => {
      let changed = false;

      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node instanceof HTMLElement) {
            markDom(node);
            changed = true;
          }
        }
      }

      if (changed) {
        if (mutationTimeout) window.clearTimeout(mutationTimeout);
        mutationTimeout = window.setTimeout(() => {
          recordUi({
            type: "mutation",
            data: {
              elements: scan().length
            }
          });
        }, 150);
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    patchFetch();
    patchHistory();

    recordUi({
      type: "snapshot",
      data: {
        elements: scan().length
      }
    });

    console.info("[du-runtime-agent] started");
  }

  function stop(): void {
    if (!started) return;

    started = false;

    document.removeEventListener("click", handleClick, true);
    document.removeEventListener("input", handleInput, true);
    document.removeEventListener("change", handleChange, true);
    document.removeEventListener("submit", handleSubmit, true);
    document.removeEventListener("keydown", handleKeydown, true);

    observer?.disconnect();
    observer = undefined;

    unpatchFetch();
    unpatchHistory();

    console.info("[du-runtime-agent] stopped");
  }

  async function waitForSelector(
    selector: string,
    timeoutMs = 5000
  ): Promise<HTMLElement> {
    const startedAt = Date.now();

    while (Date.now() - startedAt < timeoutMs) {
      try {
        const element = findTarget(selector);
        if (element && isVisible(element)) {
          return element;
        }
      } catch {
        // Ignora erros de seleção na busca assíncrona
      }

      await delay(50);
    }

    throw new Error(`Timeout esperando selector: ${selector}`);
  }

  async function waitForText(
    text: string,
    timeoutMs = 5000
  ): Promise<DuElementInfo> {
    const startedAt = Date.now();

    while (Date.now() - startedAt < timeoutMs) {
      try {
        const element = findByText(text);
        return getElementInfo(element);
      } catch {
        await delay(50);
      }
    }

    throw new Error(`Timeout esperando texto: ${text}`);
  }

  async function moveCursorTo(target: string | HTMLElement): Promise<void> {
    const element = findTarget(target);
    const cursor = createCursor();
    const rect = element.getBoundingClientRect();

    cursor.style.left = `${rect.x + rect.width / 2}px`;
    cursor.style.top = `${rect.y + rect.height / 2}px`;

    await delay(260);
  }

  async function hover(target: string | HTMLElement): Promise<void> {
    const element = findTarget(target);

    await moveCursorTo(element);

    dispatchMouseEvent(element, "mouseover");
    dispatchMouseEvent(element, "mousemove");

    recordUi({
      type: "hover",
      target: getElementInfo(element)
    });

    await delay(150);
  }

  async function click(target: string | HTMLElement): Promise<void> {
    const element = findTarget(target);

    element.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center"
    });

    await delay(250);
    await moveCursorTo(element);

    dispatchMouseEvent(element, "mouseover");
    dispatchMouseEvent(element, "mousemove");
    dispatchMouseEvent(element, "mousedown");
    dispatchMouseEvent(element, "mouseup");
    dispatchMouseEvent(element, "click");

    recordUi({
      type: "agent.click",
      target: getElementInfo(element)
    });

    await delay(300);
  }

  async function clickByText(text: string): Promise<void> {
    const element = findByText(text);
    await click(element);
  }

  async function fill(target: string | HTMLElement, value: string): Promise<void> {
    const element = findTarget(target);

    element.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center"
    });

    await delay(250);
    await moveCursorTo(element);

    element.focus();

    await delay(100);

    setNativeValue(element, value);

    recordUi({
      type: "agent.fill",
      target: getElementInfo(element),
      value: sanitizeValue(element, value)
    });

    await delay(250);
  }

  async function fillByLabel(label: string, value: string): Promise<void> {
    const element = findByLabel(label);
    await fill(element, value);
  }

  async function select(target: string | HTMLElement, value: string): Promise<void> {
    const element = findTarget(target);

    if (!(element instanceof HTMLSelectElement)) {
      throw new Error("Elemento não é select.");
    }

    element.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });

    await delay(250);
    await moveCursorTo(element);

    setNativeValue(element, value);

    recordUi({
      type: "agent.select",
      target: getElementInfo(element),
      value
    });

    await delay(250);
  }

  async function scrollTo(target: string | HTMLElement): Promise<void> {
    const element = findTarget(target);

    element.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center"
    });

    recordUi({
      type: "agent.scrollTo",
      target: getElementInfo(element)
    });

    await delay(500);
  }

  function clear(): void {
    uiEvents.splice(0, uiEvents.length);
    networkEvents.splice(0, networkEvents.length);
  }

  function exportData(): DuAgentExport {
    return {
      generatedAt: new Date().toISOString(),
      url: window.location.href,
      elements: scan(),
      events: [...uiEvents],
      network: [...networkEvents]
    };
  }

  function generateCpf(formatted = true): string {
    const num = Array.from({ length: 9 }, () => Math.floor(Math.random() * 10));
    let d1 = 0;
    for (let i = 0; i < 9; i++) {
      d1 += num[i] * (10 - i);
    }
    d1 = 11 - (d1 % 11);
    if (d1 >= 10) d1 = 0;
    num.push(d1);

    let d2 = 0;
    for (let i = 0; i < 10; i++) {
      d2 += num[i] * (11 - i);
    }
    d2 = 11 - (d2 % 11);
    if (d2 >= 10) d2 = 0;
    num.push(d2);

    const raw = num.join("");
    if (!formatted) return raw;
    return `${raw.slice(0, 3)}.${raw.slice(3, 6)}.${raw.slice(6, 9)}-${raw.slice(9)}`;
  }

  function generateCnpj(formatted = true): string {
    const num = Array.from({ length: 8 }, () => Math.floor(Math.random() * 10));
    num.push(0, 0, 0, 1);

    const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let d1 = 0;
    for (let i = 0; i < 12; i++) {
      d1 += num[i] * w1[i];
    }
    d1 = 11 - (d1 % 11);
    if (d1 >= 10) d1 = 0;
    num.push(d1);

    const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let d2 = 0;
    for (let i = 0; i < 13; i++) {
      d2 += num[i] * w2[i];
    }
    d2 = 11 - (d2 % 11);
    if (d2 >= 10) d2 = 0;
    num.push(d2);

    const raw = num.join("");
    if (!formatted) return raw;
    return `${raw.slice(0, 2)}.${raw.slice(2, 5)}.${raw.slice(5, 8)}/${raw.slice(8, 12)}-${raw.slice(12)}`;
  }

  function generateName(): string {
    const firstNames = ["Ana", "Bruno", "Carla", "Daniel", "Eliana", "Felipe", "Gabriela", "Hugo", "Isabel", "Joao", "Lucas", "Maria", "Pedro", "Rita", "Sandra", "Tiago", "Vania"];
    const lastNames = ["Silva", "Santos", "Oliveira", "Souza", "Rodrigues", "Ferreira", "Alves", "Pereira", "Costa", "Gomes", "Martins", "Rocha", "Ribeiro"];

    const first = firstNames[Math.floor(Math.random() * firstNames.length)];
    const last = lastNames[Math.floor(Math.random() * lastNames.length)];
    return `${first} ${last}`;
  }

  function generateEmail(profile = "user"): string {
    const cleanProfile = profile.toLowerCase().trim();
    const prefixMap: Record<string, string> = {
      master: "mast",
      rede: "rede",
      estado: "est",
      municipio: "mun",
      escola: "esc",
      diretor: "dir",
      secretaria: "sec",
      coordenacao: "coord",
      professor: "prof",
      responsavel: "resp",
      aluno: "aluno",
      equipe: "eqp"
    };

    const prefix = prefixMap[cleanProfile] || "usr";
    const rand = Math.random().toString(36).slice(2, 6);
    return `${prefix}.${rand}@demo.com`;
  }

  function getPassword(): string {
    return "123456";
  }

  function getPhone(): string {
    return "99 9999 9999";
  }

  const api: DuAgentApi = {
    start,
    stop,

    scan,
    events: () => [...uiEvents],
    network: () => [...networkEvents],
    export: exportData,

    wait: delay,
    waitForText,
    waitForSelector,

    click,
    clickByText,
    fill,
    fillByLabel,
    select,
    hover,
    scrollTo,
    moveCursorTo,

    clear,

    generateCpf,
    generateCnpj,
    generateName,
    generateEmail,
    getPassword,
    getPhone
  };

  window.du = api;

  return api;
}
